import React, { useEffect, useState } from "react";
import { FormInput } from "best-common-react";
import styled from "styled-components";
import CardSettingsOptions from "./CardSettingsOptions";
import SettingsApi from "../../../httpClients/SettingsApi";
import { useAlert } from "../../alerts/AlertsContext";
import { PrimaryOutlinedButtonStyled } from "../../elements/PrimaryOutlinedButton";

const colorDropdownOptions = {
  black: { value: "#000000", label: "Black" },
  green: { value: "#1fec3b", label: "Green" },
  red: { value: "#ec2735", label: "Red" },
  blue: { value: "#33aaff", label: "Blue" },
};

const colorDropdownOpts = [
  colorDropdownOptions.black,
  colorDropdownOptions.red,
  colorDropdownOptions.green,
  colorDropdownOptions.blue,
];

const findColorOption = (hex) =>
  colorDropdownOpts.find((opt) => opt.value === hex) || colorDropdownOptions.red;

const Title = styled.h5.attrs(() => ({
  className: "mt-2",
}))`
  &&& {
    color: ${(props) => (props.color ? props.color : "#000000")};
    width: 100px;
  }
`;

const SelectHolder = styled.div`
  width: 15rem;
`;

const FooterStyle = styled.div.attrs(() => ({
  className: "d-flex justify-content-end align-items-center mt-4",
}))``;

const ColorSelector = ({ type, value, onChange }) => {
  const color = type === "Switch" ? value?.switchHitter : value?.[type.toLowerCase()];

  return (
    <div>
      {color ? (
        <div className="mt-3 d-flex">
          <Title color={color.value}>{type}</Title>
          <SelectHolder>
            <FormInput
              name="color-select"
              type="select"
              isRequired={false}
              isDisabled={false}
              label=""
              editable={true}
              value={color}
              options={colorDropdownOpts}
              onChangeFn={(value) => onChange(type, value)}
            />
          </SelectHolder>
        </div>
      ) : null}
    </div>
  );
};

const CardSettings = () => {
  const showAlert = useAlert();

  const [colorOptions, setColorOptions] = useState({
    lefty: colorDropdownOptions.red,
    righty: colorDropdownOptions.black,
    switchHitter: colorDropdownOptions.blue,
  });
  const [logoTransparency, setLogoTransparency] = useState(0.25);
  const [lastNameOnly, setLastNameOnly] = useState(false);
  const [fullWidthImage, setFullWidthImage] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    SettingsApi.getCardSettings()
      .then((data) => {
        setColorOptions({
          lefty: findColorOption(data.leftyColor),
          righty: findColorOption(data.rightyColor),
          switchHitter: findColorOption(data.switchHitterColor),
        });
        setLogoTransparency(data.logoTransparency ?? 0.25);
        setLastNameOnly(data.lastNameOnly ?? false);
        setFullWidthImage(data.fullWidthImage ?? false);
      })
      .catch(() => {
        showAlert("Error retrieving card settings. Using defaults.", "danger");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleColorChange = (type, value) => {
    if (type === "Switch") {
      setColorOptions((prev) => ({ ...prev, switchHitter: value }));
    } else {
      setColorOptions((prev) => ({ ...prev, [type.toLowerCase()]: value }));
    }
  };

  const handleSave = () => {
    const payload = {
      leftyColor: colorOptions.lefty.value,
      rightyColor: colorOptions.righty.value,
      switchHitterColor: colorOptions.switchHitter.value,
      logoTransparency,
      lastNameOnly,
      fullWidthImage,
    };
    SettingsApi.setCardSettings(payload)
      .then(() => {
        showAlert("Saved", "success");
      })
      .catch(() => {
        showAlert("Error saving card settings. Please try again.", "danger");
      });
  };

  if (loading) return null;

  return (
    <div>
      <div>
        <div className="col mb-2">
          <ColorSelector type="Righty" value={colorOptions} onChange={handleColorChange} />
          <ColorSelector type="Lefty" value={colorOptions} onChange={handleColorChange} />
          <ColorSelector type="Switch" value={colorOptions} onChange={handleColorChange} />
        </div>
        <CardSettingsOptions
          logoTransparency={logoTransparency}
          lastNameOnly={lastNameOnly}
          fullWidthImage={fullWidthImage}
          onLogoTransparencyChange={setLogoTransparency}
          onLastNameOnlyChange={setLastNameOnly}
          onFullWidthImageChange={setFullWidthImage}
        />
      </div>
      <FooterStyle>
        <PrimaryOutlinedButtonStyled height={26} width={160} onClick={handleSave}>
          Save Settings
        </PrimaryOutlinedButtonStyled>
      </FooterStyle>
    </div>
  );
};

export default CardSettings;
